import { useEffect } from "react";
import { useAuth as useClerkAuth } from "@clerk/react";
import type { AppAudience, UserType } from "@amsaas/shared";
import { trpc } from "../lib/trpc";
import { applyBrand } from "./useTenantBrand";

/**
 * Resolves the signed-in user for the ACTIVE tenant via auth.me (ENH-004) and
 * applies that tenant's brand. Routing + branding key off this — not the single
 * global Clerk publicMetadata — so an identity that belongs to several providers
 * resolves correctly for whichever org is active, and re-resolves on org switch.
 */
export interface ActiveUser {
  isLoading: boolean;
  isError: boolean;
  isSignedIn: boolean;
  userType: UserType | null;
  /** App(s) this identity may use, for the host guard (GAP-025). Null until resolved. */
  appAudience: AppAudience | null;
  platformName: string;
}

export function useActiveUser(): ActiveUser {
  const { isLoaded, isSignedIn } = useClerkAuth();
  const me = trpc.auth.me.useQuery(undefined, {
    retry: false,
    enabled: isLoaded && Boolean(isSignedIn),
  });

  useEffect(() => {
    if (me.data?.tenant.brand) applyBrand(me.data.tenant.brand);
  }, [me.data]);

  // eslint-disable-next-line no-console -- temporary auth debug
  console.log("[auth-debug] useActiveUser", {
    isLoaded,
    isSignedIn,
    status: me.status,
    fetchStatus: me.fetchStatus,
    userType: me.data?.user.userType ?? null,
    error: me.error ? `${me.error.data?.code ?? ""} ${me.error.message}` : null,
  });

  return {
    isLoading:
      !isLoaded ||
      (Boolean(isSignedIn) && me.isLoading && me.fetchStatus !== "idle"),
    isError: me.isError,
    isSignedIn: Boolean(isSignedIn),
    userType: me.data?.user.userType ?? null,
    appAudience: me.data?.appAudience ?? null,
    platformName: me.data?.tenant.brand.platformDisplayName ?? "AMSaaS Portal",
  };
}
